import { type Nurse, type NurseFullRole, WageType } from "./models";

/** Each role's average of `hourlyWageOf` across `nurses`, to 2 decimals; a role nobody has one for is left out. */
const roleAveragesOf = (nurses: readonly Nurse[], hourlyWageOf: (nurse: Nurse) => number | undefined): Partial<Record<NurseFullRole, string>> => {
	const wagesByRole: Partial<Record<NurseFullRole, number[]>> = {};
	for (const nurse of nurses) {
		const hourlyWage = hourlyWageOf(nurse);
		if (hourlyWage !== undefined && nurse.role) wagesByRole[nurse.role] = [...(wagesByRole[nurse.role] ?? []), hourlyWage];
	}
	const averageWages: Partial<Record<NurseFullRole, string>> = {};
	for (const [role, wages] of Object.entries(wagesByRole)) {
		// Object.entries widens the keys to string; they are the roles written above.
		averageWages[role as NurseFullRole] = (wages.reduce((sum, wage) => sum + wage, 0) / wages.length).toFixed(2);
	}
	return averageWages;
};

/** Each role's average current hourly wage across `nurses`, to 2 decimals; a role nobody has a current hourly wage for is left out. */
export const roleAverageWagesOf = (nurses: readonly Nurse[]): Partial<Record<NurseFullRole, string>> =>
	roleAveragesOf(
		nurses,
		(nurse) => nurse.baseWages?.find((baseWage) => baseWage.wage > 0 && baseWage.wageType !== WageType.Annually && !baseWage.endTime)?.wage,
	);

/**
 * The same from each nurse's latest wage (`wage`, `wageType`), for a staff list sent without the wage history: the
 * schedule cache's. A wage that has since ended still counts, where the history would leave it out.
 */
export const roleAverageLatestWagesOf = (nurses: readonly Nurse[]): Partial<Record<NurseFullRole, string>> =>
	roleAveragesOf(nurses, (nurse) => (nurse.wage !== undefined && nurse.wage > 0 && nurse.wageType !== WageType.Annually ? nurse.wage : undefined));
